if (!customElements.get('home-hero-slideshow')) {
  customElements.define(
    'home-hero-slideshow',
    class HomeHeroSlideshow extends HTMLElement {
      connectedCallback() {
        this.slides = Array.from(this.querySelectorAll('[data-slide]'));
        this.dots = Array.from(this.querySelectorAll('[data-dot]'));
        this.current = Math.max(
          0,
          this.slides.findIndex((slide) => slide.classList.contains('is-active'))
        );
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.modal = this.querySelector('.video-modal');
        this.modalOpen = false;

        this.onDotClick = (event) => this.goTo(Number(event.currentTarget.dataset.dot), true);
        this.onPause = () => this.stop();
        this.onResume = () => this.start();
        this.onBlockSelect = (event) => {
          const index = this.slides.indexOf(event.target);
          if (index > -1) {
            this.goTo(index, true);
            this.stop();
          }
        };
        this.onBlockDeselect = () => this.start();
        this.onVisibility = () => (document.hidden ? this.stop() : this.start());

        this.onClick = (event) => {
          if (event.target.closest('[data-action="open-video-modal"]')) {
            event.preventDefault();
            this.openModal(event.target.closest('[data-action="open-video-modal"]'));
          } else if (event.target.closest('[data-action="close-video-modal"]')) {
            event.preventDefault();
            this.closeModal();
          }
        };
        this.onKeydown = (event) => {
          if (!this.modalOpen) return;
          if (event.key === 'Escape') {
            this.closeModal();
          } else if (event.key === 'Tab') {
            this.trapFocus(event);
          }
        };

        this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));
        this.addEventListener('click', this.onClick);
        document.addEventListener('keydown', this.onKeydown);
        this.addEventListener('mouseenter', this.onPause);
        this.addEventListener('mouseleave', this.onResume);
        this.addEventListener('focusin', this.onPause);
        this.addEventListener('focusout', this.onResume);
        this.addEventListener('shopify:block:select', this.onBlockSelect);
        this.addEventListener('shopify:block:deselect', this.onBlockDeselect);
        document.addEventListener('visibilitychange', this.onVisibility);

        this.start();
      }

      disconnectedCallback() {
        this.stop();
        document.removeEventListener('keydown', this.onKeydown);
        if (this.modalOpen) document.body.style.overflow = '';
        this.dots.forEach((dot) => dot.removeEventListener('click', this.onDotClick));
        document.removeEventListener('visibilitychange', this.onVisibility);
      }

      get shouldAutoplay() {
        return this.dataset.autoplay === 'true' && this.slides.length > 1 && !this.reducedMotion;
      }

      start() {
        this.stop();
        if (this.modalOpen || !this.shouldAutoplay) return;
        const seconds = Number(this.dataset.speed) || 6;
        this.timer = setInterval(() => this.goTo(this.current + 1), seconds * 1000);
      }

      stop() {
        clearInterval(this.timer);
      }

      openModal(trigger) {
        if (!this.modal) return;
        this.modalTrigger = trigger;
        this.modalOpen = true;
        this.stop();
        this.modal.classList.add('is-open');
        this.modal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';

        const video = this.modal.querySelector('video');
        if (video) {
          video.currentTime = 0;
          const playing = video.play();
          if (playing) playing.catch(() => {});
        }

        const iframe = this.modal.querySelector('iframe[data-src]');
        if (iframe) {
          const src = iframe.dataset.src;
          iframe.src = `${src}${src.includes('?') ? '&' : '?'}autoplay=1`;
        }

        const close = this.modal.querySelector('.video-modal__close');
        if (close) setTimeout(() => close.focus(), 50);
      }

      closeModal() {
        if (!this.modal || !this.modalOpen) return;
        this.modalOpen = false;
        this.modal.classList.remove('is-open');
        this.modal.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';

        const video = this.modal.querySelector('video');
        if (video) video.pause();

        const iframe = this.modal.querySelector('iframe[data-src]');
        if (iframe) iframe.src = '';

        if (this.modalTrigger) this.modalTrigger.focus();
        this.start();
      }

      trapFocus(event) {
        const focusable = Array.from(
          this.modal.querySelectorAll('button, [href], video[controls], iframe, [tabindex]:not([tabindex="-1"])')
        );
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      goTo(index, restart) {
        const next = (index + this.slides.length) % this.slides.length;
        this.slides.forEach((slide, i) => {
          const active = i === next;
          slide.classList.toggle('is-active', active);
          slide.toggleAttribute('inert', !active);
        });
        this.dots.forEach((dot, i) => {
          const active = i === next;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-current', active ? 'true' : 'false');
        });
        this.current = next;
        if (restart) this.start();
      }
    }
  );
}
